import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { InvoiceWithRelationsDto, ResponseDto } from "@mimber/schema"
import { invoicesKeys } from "../invoices.utils"

interface DeleteLineItemVariables {
  lineItemUuid: string
}

interface DeleteInvoiceLineItemOptions extends Omit<
  UseMutationOptions<
    ResponseDto<InvoiceWithRelationsDto>,
    Error,
    DeleteLineItemVariables
  >,
  "mutationFn"
> {}

export const useDeleteInvoiceLineItem = (
  invoiceUuid: string,
  options: DeleteInvoiceLineItemOptions = {}
): UseMutationResult<
  ResponseDto<InvoiceWithRelationsDto>,
  Error,
  DeleteLineItemVariables
> => {
  guardAppUsage("mimber-organization", "useDeleteInvoiceLineItem")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ lineItemUuid }: DeleteLineItemVariables) => {
      const response = await server.delete<
        ResponseDto<InvoiceWithRelationsDto>
      >(`invoices/${invoiceUuid}/line-items/${lineItemUuid}`)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: invoicesKeys.findOne(invoiceUuid),
      })
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findAll() })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
